<template>
  <AuthWrapper>
    <template #header>
      <template v-if="!emailSentRef">
        <h1 class="text-2xl" v-html="t('auth.forgotPassword.title')"></h1>
        <p v-if="!tokenRef" class="text-sm text-muted-foreground" v-html="t('auth.forgotPassword.body')"></p>
      </template>
    </template>
    <template #body>
      <Card>
        <CardContent>
          <Form
            v-if="tokenRef"
            :validation-schema="resetSchema"
            :initial-values="{ password: '', confirmPassword: '' }"
            class="flex flex-col gap-6"
            as="form"
            @submit="onResetSubmit"
          >
            <FormField v-slot="{ componentField }" name="password" :validate-on-input="true">
              <FormItem>
                <FormLabel>{{ t('ui.input.password.label') }}</FormLabel>
                <InputGroup>
                  <FormControl>
                    <InputGroupInput
                      :type="showPasswordRef ? 'text' : 'password'"
                      data-test-id="forgot-password_input-password"
                      :placeholder="t('ui.input.password.placeholder')"
                      v-bind="componentField"
                    />
                  </FormControl>
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      type="button"
                      size="icon-xs"
                      data-test-id="forgot-password_button-toggle-password"
                      :aria-label="showPasswordRef ? t('ui.input.password.hide') : t('ui.input.password.show')"
                      @click="showPasswordRef = !showPasswordRef"
                    >
                      <EyeOffIcon v-if="showPasswordRef" />
                      <EyeIcon v-else />
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
                <FormMessage />
              </FormItem>
            </FormField>

            <FormField v-slot="{ componentField }" name="confirmPassword" :validate-on-input="true">
              <FormItem>
                <FormLabel>{{ t('auth.password.modify.confirmNewPassword') }}</FormLabel>
                <InputGroup>
                  <FormControl>
                    <InputGroupInput
                      :type="showConfirmPasswordRef ? 'text' : 'password'"
                      data-test-id="forgot-password_input-confirm-password"
                      :placeholder="t('auth.password.modify.confirmNewPassword')"
                      v-bind="componentField"
                    />
                  </FormControl>
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      type="button"
                      size="icon-xs"
                      data-test-id="forgot-password_button-toggle-confirm-password"
                      :aria-label="showConfirmPasswordRef ? t('ui.input.password.hide') : t('ui.input.password.show')"
                      @click="showConfirmPasswordRef = !showConfirmPasswordRef"
                    >
                      <EyeOffIcon v-if="showConfirmPasswordRef" />
                      <EyeIcon v-else />
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
                <FormMessage />
              </FormItem>
            </FormField>

            <div class="mt-2 flex flex-col gap-6">
              <Button type="submit" data-test-id="forgot-password_button-submit" class="w-full">
                {{ t('auth.forgotPassword.resetPassword') }}
              </Button>
            </div>
          </Form>

          <div v-else-if="emailSentRef" class="flex flex-col items-center gap-5 text-center">
            <MailCheckIcon class="size-9 text-primary" />
            <div class="flex flex-col gap-1">
              <p class="text-sm text-muted-foreground">{{ t('auth.forgotPassword.checkEmail.intro') }}</p>
              <p class="text-sm font-semibold">{{ submittedEmailRef }}</p>
            </div>
            <button
              type="button"
              data-test-id="forgot-password_button-resend"
              class="text-sm font-medium !text-primary !no-underline hover:!underline"
              @click="onResendEmail"
            >
              {{ t('auth.forgotPassword.checkEmail.resend') }}
            </button>
          </div>

          <Form
            v-else
            ref="emailFormRef"
            :validation-schema="emailSchema"
            :initial-values="{ email: initialEmail }"
            class="flex flex-col gap-6"
            as="form"
            @submit="onEmailSubmit"
          >
            <FormField
              v-slot="{ componentField }"
              name="email"
              :validate-on-model-update="false"
              :validate-on-blur="false"
            >
              <FormItem>
                <FormLabel>{{ t('ui.input.email.label') }}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    data-test-id="forgot-password_input-email"
                    :placeholder="t('ui.input.email.placeholder')"
                    v-bind="componentField"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>

            <div class="mt-2 flex flex-col items-center gap-4">
              <Button type="submit" data-test-id="forgot-password_send-email-submit" class="w-full">
                {{ t('auth.forgotPassword.sendEmail') }}
              </Button>
              <router-link
                :to="ROUTES.INDEX"
                data-test-id="forgot-password_button-back"
                class="inline-flex items-center gap-1.5 text-sm font-medium !text-primary !no-underline hover:!underline"
              >
                <ArrowLeftIcon class="size-4" />
                {{ t('auth.forgotPassword.backToLogin') }}
              </router-link>
            </div>
          </Form>
        </CardContent>
      </Card>
      <p v-if="whiteLabelRef.supportEmail" class="mt-6 text-center text-sm text-muted-foreground">
        {{ t('auth.forgotPassword.contactSupport.text') }}
        <a
          :href="`mailto:${whiteLabelRef.supportEmail}`"
          class="font-medium !text-primary !no-underline hover:!underline"
          >{{ whiteLabelRef.supportEmail }}</a
        >
      </p>
    </template>
  </AuthWrapper>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { toTypedSchema } from '@vee-validate/zod'
import { ArrowLeftIcon, EyeIcon, EyeOffIcon, MailCheckIcon } from 'lucide-vue-next'
import { Form } from 'vee-validate'
import { ref } from 'vue'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group'
import { resetPasswordMutation, sendEmailForgotPasswordMutation } from '@/features/auth/api'
import AuthWrapper from '@/features/auth/components/auth-wrapper/auth-wrapper.vue'
import authStore from '@/features/auth/stores/auth/auth.store'
import useRouter from '@/hooks/use-router.hook'
import useToast from '@/hooks/use-toasts.hook'
import useWhiteLabel from '@/hooks/use-white-label.hook'
import { useI18n } from '@/lib/i18n'
import { EMAIL_REGEX, ROUTES } from '@/utils/config/constants'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { whiteLabelRef } = useWhiteLabel()

const initialToken = (router.currentRoute?.params?.token as string | undefined) || null
const initialEmail = (router.currentRoute?.query?.email as string | undefined) || ''

const tokenRef = ref<string | null>(initialToken)
const emailSentRef = ref(false)
const submittedEmailRef = ref('')
const showPasswordRef = ref(false)
const showConfirmPasswordRef = ref(false)
const emailFormRef = ref<InstanceType<typeof Form> | null>(null)

const emailSchema = toTypedSchema(
  z.object({
    email: z
      .string({ required_error: t('error.requiredEmailField') })
      .min(1, t('error.requiredEmailField'))
      .regex(EMAIL_REGEX, t('auth.login.email.errors.invalid')),
  }),
)

const resetSchema = toTypedSchema(
  z
    .object({
      password: z
        .string({ required_error: t('auth.password.errors.required') })
        .min(8, t('auth.password.errors.minLength'))
        .regex(/(?=.*[a-z])/, t('auth.password.errors.lowercase'))
        .regex(/(?=.*[A-Z])/, t('auth.password.errors.uppercase'))
        .regex(/(?=.*[0-9])/, t('auth.password.errors.number'))
        .regex(/(?=.*[@$!%*?&\-_#])/, t('auth.password.errors.specialChar')),
      confirmPassword: z.string({ required_error: t('auth.password.errors.required') }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ['confirmPassword'],
      message: t('auth.forgotPassword.error.passwordsDoNotMatch'),
    }),
)

const { mutate: sendEmailForgotPassword } = useMutation({
  mutationFn: sendEmailForgotPasswordMutation,
  onSuccess: () => {
    emailSentRef.value = true
  },
  onError: (error: any) => {
    if (error?.status === 429) {
      emailFormRef.value?.setFieldError('email', t('auth.forgotPassword.error.rateLimitExceeded'))
      return
    }
    // Anti user-enumeration: never reveal that the email is unknown.
    // For any other failure (network, 5xx, etc.), show the same generic confirmation.
    emailSentRef.value = true
  },
})

const { mutate: resetPassword } = useMutation({
  mutationFn: resetPasswordMutation,
  onSuccess: async (data: any) => {
    if (data?.token) {
      try {
        await authStore.setAuthToken(data.token)
        toast?.success(t('auth.forgotPassword.success.passwordResetAndLoggedIn'))
        router?.push(ROUTES.REDIRECT)
      } catch {
        toast?.success(t('auth.forgotPassword.success.passwordReset'))
        router?.push(ROUTES.INDEX)
      }
    } else {
      toast?.success(t('auth.forgotPassword.success.passwordReset'))
      router?.push(ROUTES.INDEX)
    }
  },
  onError: (error: any) => {
    const errorMessage = error?.message || ''

    if (errorMessage.includes('expired') || errorMessage.includes('Token has expired')) {
      toast?.error(t('auth.forgotPassword.error.tokenExpired'))
    } else if (errorMessage.includes('used') || errorMessage.includes('already been used')) {
      toast?.error(t('auth.forgotPassword.error.tokenAlreadyUsed'))
    } else if (errorMessage.includes('Invalid') || errorMessage.includes('invalid')) {
      toast?.error(t('auth.forgotPassword.error.tokenInvalid'))
    } else {
      toast?.error(t('auth.forgotPassword.error.generic'))
    }
  },
})

function onEmailSubmit(values: Record<string, any>) {
  const email = (values.email as string).trim()
  submittedEmailRef.value = email
  sendEmailForgotPassword(email)
}

function onResetSubmit(values: Record<string, any>) {
  if (!tokenRef.value) return
  resetPassword({
    token: tokenRef.value,
    password: values.password as string,
    confirmPassword: values.confirmPassword as string,
  })
}

function onResendEmail() {
  emailSentRef.value = false
}
</script>
